import { useState, useEffect, useRef, useId } from 'react';
import styles from './collections-popover.module.css';
import { Flex, Text, Tag, Input, useReducedMotion } from '@nula/core-ui';
import { motion, AnimatePresence } from 'motion/react';
import { useWorkspaceDoc } from '@/lib/sync/hooks/use-workspace-doc';
import { useWorkspaceCollections } from '@/lib/sync/hooks/use-workspace-collections';
import { sessionOpenTab } from '@/session';

const MAX_RESULTS = 5;

interface CollectionsPopoverProps {
  open: boolean;
  onClose: () => void;
  entityId: string;
  onAddCollection?: (collectionId: string) => void;
  onRemoveCollection?: (collectionId: string) => void;
}

export function CollectionsPopover({ open, onClose, entityId, onAddCollection, onRemoveCollection }: CollectionsPopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const shouldReduce = useReducedMotion();
  const [search, setSearch] = useState('');

  const doc = useWorkspaceDoc(entityId);
  const collectionIds = doc?.collectionIds ?? [];
  const allCollections = useWorkspaceCollections();

  const currentCollections = allCollections.filter((c) => collectionIds.includes(c.id));
  const results = allCollections
    .filter((c) => !collectionIds.includes(c.id))
    .filter((c) => c.name.toLowerCase().includes(search.toLowerCase()))
    .slice(0, MAX_RESULTS);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current?.contains(e.target as Node)) return;
      onClose();
    };
    const timer = setTimeout(() => document.addEventListener('mousedown', handler), 50);
    return () => { clearTimeout(timer); document.removeEventListener('mousedown', handler); };
  }, [open, onClose]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Reset search when closed
  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={panelRef}
          role="dialog"
          aria-labelledby={titleId}
          aria-modal="true"
          initial={shouldReduce ? { opacity: 0 } : { opacity: 0, y: 4 }}
          animate={shouldReduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={shouldReduce ? { opacity: 0 } : { opacity: 0, y: 4 }}
          transition={{ duration: 0.15 }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          className={styles.panel}
        >
          <Flex vertical className={styles.content}>
            <Text id={titleId} size="xs" strong className={styles.title}>
              Collections
            </Text>

            {/* Current collection chips */}
            {currentCollections.length > 0 && (
              <Flex wrap gap="xs" align="center" className={styles.chips}>
                {currentCollections.map((c) => (
                  <Tag
                    key={c.id}
                    bordered={false}
                    closable
                    onClose={() => onRemoveCollection?.(c.id)}
                    onClick={() => { sessionOpenTab(c.id, 'collection'); onClose(); }}
                    className={styles.tagStyle}
                  >
                    {c.name}
                  </Tag>
                ))}
              </Flex>
            )}

            {/* Search input */}
            <Input
              autoFocus
              size="sm"
              variant="plain"
              aria-label="Search collections"
              placeholder="Search collections…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={styles.searchInput}
            />

            {/* Results */}
            {results.length > 0 && (
              <Flex vertical className={styles.results}>
                {results.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className={styles.resultOption}
                    onClick={() => { onAddCollection?.(c.id); setSearch(''); }}
                  >
                    {c.name}
                  </button>
                ))}
              </Flex>
            )}
          </Flex>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
